import React, { useState, useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

const CategoryTrendChart = ({ data }) => {
  // Dynamically extract all possible categories
  const allCategories = useMemo(() => {
    if (data.length === 0) return [];
    
    return Object.keys(data[0])
      .filter(key => key !== 'month')
      .reduce((acc, key) => {
        const [category] = key.split('_');
        if (!acc.includes(category)) {
          acc.push(category);
        }
        return acc;
      }, []);
  }, [data]);

  // State for selections
  const [selectedCategories, setSelectedCategories] = useState(allCategories);
  const [sentimentType, setSentimentType] = useState('positive');

  // Color palette for lines
  const colorPalette = [
    '#3B82F6',  // Blue
    '#10B981', // Green
    '#F43F5E', // Red
    '#8B5CF6', // Purple
    '#F59E0B', // Yellow
    '#22D3EE', // Cyan
    '#EC4899'  // Pink
  ];

  // Custom dropdown to select categories
  const CategoryDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="relative">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm flex items-center justify-between w-48"
        >
          <span>
            {selectedCategories.length === allCategories.length 
              ? 'All Categories' 
              : selectedCategories.join(', ')}
          </span>
          <svg 
            className="w-4 h-4 ml-2" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 9l-7 7-7-7" 
            />
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute z-10 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {/* Select All */}
            <label 
              className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer border-b"
            >
              <input 
                type="checkbox" 
                checked={selectedCategories.length === allCategories.length}
                onChange={() => {
                  setSelectedCategories(
                    selectedCategories.length === allCategories.length 
                      ? [] 
                      : [...allCategories]
                  );
                }}
                className="mr-2"
              />
              <span>Select All</span>
            </label>

            {/* Individual Categories */}
            {allCategories.map(category => (
              <label 
                key={category} 
                className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <input 
                  type="checkbox" 
                  checked={selectedCategories.includes(category)}
                  onChange={() => {
                    setSelectedCategories(prev => 
                      prev.includes(category)
                        ? prev.filter(c => c !== category)
                        : [...prev, category]
                    );
                  }}
                  className="mr-2"
                />
                <span>{category}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-lg max-w-md">
          <h4 className="font-bold text-gray-800 mb-2">{label}</h4>
          {payload.map((entry, index) => (
            <div key={`item-${index}`} className="flex justify-between mb-1">
              <span className="mr-4 flex-grow" style={{ color: entry.color }}>
                {entry.name}
              </span>
              <span className="font-semibold">
                {entry.value.toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-800">Category Sentiment Trends</h2>
        
        <div className="flex items-center space-x-4">
          {/* Sentiment Type Dropdown */}
          <select 
            value={sentimentType}
            onChange={(e) => setSentimentType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="positive">Positive Trend</option>
            <option value="negative">Negative Trend</option>
          </select>

          {/* Category Dropdown */}
          <CategoryDropdown />
        </div>
      </div>

      <div className="w-full h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 100]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Render lines for selected categories */}
            {selectedCategories.map((category, catIndex) => (
              <Line
                key={category}
                type="monotone"
                dataKey={`${category}_sentiment_${sentimentType}`}
                stroke={colorPalette[catIndex % colorPalette.length]}
                strokeWidth={3}
                dot={false}
                name={`${category} ${sentimentType.charAt(0).toUpperCase() + sentimentType.slice(1)}`}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CategoryTrendChart;